import { addSuccess } from '@mse-reg/app/modules/notification.slice';
import { IProfileClient, PriorityAssignmentDto, PriorityDto, ProfileDto } from '@mse-reg/clients';

import { createAppThunk } from '@mse-reg/app/store/app-thunk';
import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';
import { profileSelectionThunks } from '../selections/profile-selection.slice';

// State
const initialState = {
  loading: false,
  selectedProfile: null as ProfileDto,
  priorityAssignments: [] as PriorityAssignmentDto[],
  selectedPriority: null as PriorityDto,
  sidebarFilter: '',
};

export type ProfileManagementState = Readonly<typeof initialState>;

const slice = createSlice({
  name: 'profileManagement',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    selectProfile(state, action: PayloadAction<ProfileDto>) {
      state.selectedProfile = action.payload;
    },
    setPriorityAssignments(state, action: PayloadAction<PriorityAssignmentDto[]>) {
      state.priorityAssignments = action.payload;
    },
    setSelectedPriority(state, action: PayloadAction<PriorityDto>) {
      state.selectedPriority = action.payload;
    },
    memorizeProfileChanges(state, action: PayloadAction<ProfileDto>) {
      state.selectedProfile = action.payload;
    },
    createNewProfile(state) {
      state.priorityAssignments = [];
      state.selectedProfile = { id: 0, code: '', isActive: true, inSemesters: undefined };
    },
    setSidebarFilter(state, action: PayloadAction<string>) {
      state.sidebarFilter = action.payload;
    },
  },
  extraReducers(builder) {
    // Update Profile
    builder
      .addCase(updateProfileAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProfileAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateProfileAsync.rejected, (state) => {
        state.loading = false;
      });

    // Delete Profile
    builder
      .addCase(deleteProfileAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProfileAsync.fulfilled, (state) => {
        state.loading = false;
        state.selectedProfile = null;
      })
      .addCase(deleteProfileAsync.rejected, (state) => {
        state.loading = false;
      });

    // Add Profile
    builder
      .addCase(addProfileAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(addProfileAsync.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addProfileAsync.rejected, (state) => {
        state.loading = false;
      });

    // Fetch Priority Assignments
    builder
      .addCase(fetchPriorityAssignmentsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPriorityAssignmentsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.priorityAssignments = action.payload;
      })
      .addCase(fetchPriorityAssignmentsAsync.rejected, (state) => {
        state.loading = false;
      });

    // Copy Semester
    builder
      .addCase(copySemesterAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(copySemesterAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.priorityAssignments = action.payload;
      })
      .addCase(copySemesterAsync.rejected, (state) => {
        state.loading = false;
      });

    // Assign All Priorities
    builder
      .addCase(assignAllPrioritiesAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(assignAllPrioritiesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.priorityAssignments = action.payload;
      })
      .addCase(assignAllPrioritiesAsync.rejected, (state) => {
        state.loading = false;
      });

    // Remove All Priorities
    builder
      .addCase(removeAllPrioritiesAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeAllPrioritiesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.priorityAssignments = action.payload;
      })
      .addCase(removeAllPrioritiesAsync.rejected, (state) => {
        state.loading = false;
      });

    // Change Priority Assignment
    builder
      .addCase(changePriorityAssignmentAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(changePriorityAssignmentAsync.fulfilled, (state, action) => {
        state.loading = false;
        const updatedAssignment = action.payload;
        state.priorityAssignments = state.priorityAssignments.map((assignment) =>
          assignment.id === updatedAssignment.id ? updatedAssignment : assignment,
        );
      })
      .addCase(changePriorityAssignmentAsync.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const updateProfileAsync = createAppThunk<ProfileDto, ProfileDto, { profileClient: IProfileClient }>(
  'profiles/updateProfile',
  async (profileDto, { profileClient }, dispatch) => {
    dispatch(slice.actions.memorizeProfileChanges(profileDto));

    await profileClient.update(profileDto);
    const profiles = await dispatch(profileSelectionThunks.fetchProfilesAsync()).unwrap();
    const updatedProfile = profiles.find((p) => p.id === profileDto.id);
    if (updatedProfile) {
      dispatch(slice.actions.selectProfile(updatedProfile));
    }
    dispatch(addSuccess(`Successfully updated '${profileDto.code}'!`));
    return updatedProfile;
  },
  (resolver) => ({
    profileClient: resolver.resolve('IProfileClient'),
  }),
  (_, profileDto) => `Failed to update profile '${profileDto.code}'`,
);

export const deleteProfileAsync = createAppThunk<void, ProfileDto, { profileClient: IProfileClient }>(
  'profiles/deleteProfile',
  async (profileDto, { profileClient }, dispatch) => {
    if (!profileDto.id) return;

    await profileClient.delete(profileDto.id);
    await dispatch(profileSelectionThunks.fetchProfilesAsync());
    dispatch(slice.actions.selectProfile(null));
    dispatch(addSuccess('Successfully deleted the profile ' + profileDto.code));
  },
  (resolver) => ({
    profileClient: resolver.resolve('IProfileClient'),
  }),
  (_, profileDto) => 'An unknown error occurred while deleting the profile ' + profileDto.code,
);

export const addProfileAsync = createAppThunk<void, ProfileDto, { profileClient: IProfileClient }>(
  'profiles/addProfile',
  async (profileDto, { profileClient }, dispatch, getState) => {
    const result = await profileClient.add(profileDto);
    const profiles = await dispatch(profileSelectionThunks.fetchProfilesAsync()).unwrap();
    dispatch(slice.actions.selectProfile(profiles.find((p) => p.id === result.id)));
    await dispatch(
      fetchPriorityAssignmentsAsync({
        profileId: result.id,
        semesterId: getState().semesterSelection.selectedSemester.id,
      }),
    );
    dispatch(addSuccess('Successfully added the profile ' + profileDto.code));
  },
  (resolver) => ({
    profileClient: resolver.resolve('IProfileClient'),
  }),
  (_, profileDto) => 'An unknown error occurred while adding the profile ' + profileDto.code,
);

export const fetchPriorityAssignmentsAsync = createAppThunk<
  PriorityAssignmentDto[],
  { profileId: number; semesterId: number },
  { profileClient: IProfileClient }
>(
  'profiles/fetchPriorityAssignments',
  async (args, { profileClient }) => {
    return await profileClient.getPriorityAssignments(args.profileId, args.semesterId);
  },
  (resolver) => ({
    profileClient: resolver.resolve('IProfileClient'),
  }),
);

export const copySemesterAsync = createAppThunk<
  PriorityAssignmentDto[],
  { fromSemesterId: number; toSemesterId: number; profileId: number },
  { profileClient: IProfileClient }
>(
  'profiles/copySemester',
  async (args, { profileClient }, dispatch) => {
    await profileClient.copySemester(args.profileId, args.fromSemesterId, args.toSemesterId);
    return await dispatch(
      fetchPriorityAssignmentsAsync({ profileId: args.profileId, semesterId: args.toSemesterId }),
    ).unwrap();
  },
  (resolver) => ({
    profileClient: resolver.resolve('IProfileClient'),
  }),
);

export const assignAllPrioritiesAsync = createAppThunk<
  PriorityAssignmentDto[],
  { profileId: number; semesterId: number; priorityId: number },
  { profileClient: IProfileClient }
>(
  'profiles/assignAllPriorities',
  async (args, { profileClient }, dispatch) => {
    profileClient.assignAllPriorities(args.profileId, args.semesterId, args.priorityId);
    return await dispatch(
      fetchPriorityAssignmentsAsync({ profileId: args.profileId, semesterId: args.semesterId }),
    ).unwrap();
  },
  (resolver) => ({
    profileClient: resolver.resolve('IProfileClient'),
  }),
);

export const removeAllPrioritiesAsync = createAppThunk<
  PriorityAssignmentDto[],
  { profileId: number; semesterId: number },
  { profileClient: IProfileClient }
>(
  'profiles/removeAllPriorities',
  async (args, { profileClient }, dispatch) => {
    profileClient.removeAllPriorities(args.profileId, args.semesterId);
    return await dispatch(
      fetchPriorityAssignmentsAsync({ profileId: args.profileId, semesterId: args.semesterId }),
    ).unwrap();
  },
  (resolver) => ({
    profileClient: resolver.resolve('IProfileClient'),
  }),
);

export const changePriorityAssignmentAsync = createAppThunk<
  PriorityAssignmentDto,
  { priority: PriorityDto; priorityAssignment: PriorityAssignmentDto },
  { profileClient: IProfileClient }
>(
  'profiles/changePriorityAssignment',
  async (args, { profileClient }) => {
    if (!args.priority) {
      return;
    }
    const changedAssignment = {
      ...args.priorityAssignment,
    };
    changedAssignment.priority = args.priority;
    changedAssignment.priorityId = args.priority.id;
    await profileClient.assignPriority(
      changedAssignment.profileId,
      args.priorityAssignment.moduleExecution.id,
      args.priority.id,
    );
    return changedAssignment;
  },
  (resolver) => ({
    profileClient: resolver.resolve('IProfileClient'),
  }),
);

export const profileManagementReducer = slice.reducer;
export const profileManagementActions = slice.actions;
export const profileManagementThunks = {

  updateProfileAsync,
  deleteProfileAsync,
  addProfileAsync,
  fetchPriorityAssignmentsAsync,
  copySemesterAsync,
  assignAllPrioritiesAsync,
  removeAllPrioritiesAsync,
  changePriorityAssignmentAsync,
};

// Selectors
function getOptionalText(profile: ProfileDto): string {
  return ['name', 'title', 'description']
    .map((key) => (profile as unknown as Record<string, unknown>)[key])
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join(' ');
}

function profileMatches(profile: ProfileDto, normalizedFilter: string): boolean {
  if (!normalizedFilter) return true;
  return [profile.code, getOptionalText(profile)].join(' ').toLowerCase().includes(normalizedFilter);
}

export const selectProfileSidebarData = createSelector(
  (state: { profileManagement: { sidebarFilter: string }; profileSelection: { profiles: ProfileDto[] } }) =>
    state.profileManagement.sidebarFilter,
  (state: { profileManagement: { sidebarFilter: string }; profileSelection: { profiles: ProfileDto[] } }) =>
    state.profileSelection.profiles,
  (sidebarFilter, profiles) => {
    const normalizedFilter = sidebarFilter.trim().toLowerCase();
    const allProfiles = profiles ?? [];
    return {
      sidebarFilter,
      hasFilter: normalizedFilter.length > 0,
      activeProfiles: allProfiles.filter((p) => p.isActive && profileMatches(p, normalizedFilter)),
      inactiveProfiles: allProfiles.filter((p) => !p.isActive && profileMatches(p, normalizedFilter)),
    };
  },
);
